import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconButton, Stack, Tooltip } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { authClient } from 'api/apiClient';
import { changePassword, resetPassword } from 'redux/slices/auth/authSlice';
import { AppDispatch, RootState } from 'redux/store';
import { logoutUser } from 'redux/slices/user/userSlice';
import { FloatingField } from 'components';
import { notify } from 'components/Common/Toast';
import AuthLayaout from 'pages/Auth/AuthLayout';
import Routes from 'constants/routes';
import CustomButton from 'components/Common/Button';
import CardWrapper from 'components/Common/CardWrapper';
import MuiLoader from 'components/Common/Loader';
import DrawerLeft from 'components/Common/Drawer';

const buildFormSchema = (isResetMode: boolean) =>
    yup
        .object({
            currentPassword: isResetMode ? yup.string() : yup.string().required('Enter your current password'),
            newPassword: yup
                .string()
                .required('Enter your new password')
                .min(10, 'Password is too short - should be 10 chars minimum.')
                .max(128, 'Password is too long - should be 128 chars maximum.')
                .matches(
                    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{10,}$/,
                    'Password should have at least 1 uppercase, 1 lowercase and 1 digit.'
                ),
            confirmPassword: yup
                .string()
                .required('Confirm password')
                .min(10, 'Password is too short - should be 10 chars minimum.')
                .max(128, 'Password is too long - should be 128 chars maximum.')
                .matches(
                    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{10,}$/,
                    'Password should have at least 1 uppercase, 1 lowercase and 1 digit.'
                )
                .oneOf([yup.ref('newPassword')], 'Your passwords do not match.'),
        })
        .required();

export interface ChangePasswordFormInputs {
    currentPassword?: string;
    newPassword: string;
    confirmPassword: string;
}

export default function MyAccountChangePassword() {
    const navigation = useNavigate();
    const location = useLocation();
    const { type, resetToken, forcePasswordChange } = location?.state || {};
    const isResetMode = type === 'resetPassword';
    const isFirstLogin = forcePasswordChange === true && !isResetMode;
    const dispatch = useDispatch<AppDispatch>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const logoutLoading = useSelector((state: RootState) => state.user?.logoutLoading);
    const auth = useSelector((state: RootState) => state.auth?.data);
    const accessToken = auth?.accessToken;

    // This route isn't wrapped in ProtectedRoute (the reset-password flow reaches it unauthenticated),
    // so the "change password while logged in" case has to guard and rehydrate authClient's
    // token itself here instead - a direct/fresh load otherwise sends change-password with no
    // Authorization header, since ProtectedRoute's rehydration effect never runs for this route.
    // Public reset requires resetToken in location.state (PIN was already consumed on VerifyPinCode).
    // Refresh/direct-nav without the token must restart at forgot-password — not login, and not a
    // blind reset-password call. hadAccessTokenRef still distinguishes "never logged in" public
    // reset from "was logged in, session just ended (e.g. logout), leave the page".
    const hadAccessTokenRef = useRef(false);
    useEffect(() => {
        if (accessToken) {
            hadAccessTokenRef.current = true;
            authClient.setToken(accessToken, auth?.refreshToken);
            return;
        }
        if (isResetMode && !resetToken) {
            navigation(Routes.auth.forgotPassword);
            return;
        }
        if (!isResetMode || hadAccessTokenRef.current) {
            navigation(Routes.auth.login);
        }
    }, [isResetMode, resetToken, accessToken, auth?.refreshToken, navigation]);

    let description = `Your new password must be different from previous used passwords and must be of at least 10 characters.`;
    if (isFirstLogin) {
        description =
            'Set your permanent password. Enter the temporary password you received by email, then choose a new password you will use to sign in.';
    } else if (!isResetMode) {
        description = `Please create your permanent password that you will use to login to this account.`;
    }

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<ChangePasswordFormInputs>({
        resolver: yupResolver(buildFormSchema(isResetMode)),
        defaultValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        },
    });
    const submit = async (data: ChangePasswordFormInputs) => {
        const { currentPassword, newPassword } = data;
        if (isResetMode && !resetToken) {
            navigation(Routes.auth.forgotPassword);
            return;
        }
        setIsLoading(true);
        try {
            const result = (
                isResetMode
                    ? await dispatch(resetPassword({ resetToken, newPassword }))
                    : await dispatch(changePassword({ currentPassword: currentPassword || '', newPassword }))
            ) as { error?: unknown } | undefined;
            if (result?.error) return;
            notify('Password Changed Successfully!', 'success');

            if (isResetMode) {
                navigation(Routes.auth.login);
            } else {
                handleLogout();
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };
    const handleLogout = async () => {
        await dispatch(logoutUser());
        navigation(Routes.auth.login);
    };
    const content = () => {
        return (
            <div className="Auth-form">
                {isFirstLogin && (
                    <Tooltip title="Logout">
                        <IconButton disabled={logoutLoading} onClick={handleLogout} sx={{ position: 'absolute', top: '5px', right: '5px' }}>
                            {logoutLoading ? <MuiLoader size={20} loading={logoutLoading} /> : <LogoutIcon />}
                        </IconButton>
                    </Tooltip>
                )}
                <p className="Auth-form-title">{isFirstLogin ? 'Set your permanent password' : 'Create new password'}</p>
                <p className="Auth-form-subtitle">{description}</p>
                {!isResetMode && (
                    <Controller
                        name="currentPassword"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value } }) => (
                            <FloatingField
                                label={isFirstLogin ? 'Temporary Password' : 'Current Password'}
                                value={value || ''}
                                isPassword={true}
                                onChange={onChange}
                                error={errors.currentPassword?.message}
                            />
                        )}
                    />
                )}
                <Controller
                    name="newPassword"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                        <FloatingField
                            label="Password"
                            value={value}
                            isPassword={true}
                            onChange={onChange}
                            fieldDescription={'Must be 10-128 characters long.'}
                            error={errors.newPassword?.message}
                        />
                    )}
                />
                <Controller
                    name="confirmPassword"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                        <FloatingField
                            label="Confirm password"
                            value={value}
                            isPassword={true}
                            onChange={onChange}
                            fieldDescription={'Both passwords must match.'}
                            error={errors.confirmPassword?.message}
                        />
                    )}
                />

                <CustomButton
                    loading={isLoading}
                    title={isResetMode ? 'UPDATE PASSWORD' : 'Save'}
                    onClick={handleSubmit(submit)}
                    sx={{ fontSize: { xs: 14, sm: 18 }, mt: 2 }}
                />
            </div>
        );
    };
    // Public forgot-password reset (VerifyPinCode → resetToken, no session) uses AuthLayout.
    if (isResetMode && !accessToken) {
        return <AuthLayaout showBackButton>{content()}</AuthLayaout>;
    }
    // Forced first-login / temporary-password change happens before the user has entered the app
    // shell, so it keeps the unauthenticated-style layout (matches login/forgot-password screens).
    if (isFirstLogin) {
        return <AuthLayaout>{content()}</AuthLayaout>;
    }
    // Authenticated change from My Account, and the odd logged-in + resetToken state, render inside
    // the app shell (drawer + card) like every other authenticated screen.
    return (
        <DrawerLeft>
            <CardWrapper stackProps={{ alignItems: 'center' }}>
                <Stack
                    width={{ xs: '100%', sm: '80%', md: '60%', lg: '50%' }}
                    justifyContent="center"
                    sx={{ '& .Auth-form-title': { mt: 0 } }}
                >
                    {content()}
                </Stack>
            </CardWrapper>
        </DrawerLeft>
    );
}
